import * as React from 'react';
import type { GetTokenSilentlyOptions, LogoutOptions, RedirectLoginOptions, User } from '@auth0/auth0-react';

/**
 * Fallback Auth0-like context for when Auth0Provider is not in the component tree.
 * This allows hooks to call useAuth0 unconditionally while respecting React's Rules of Hooks.
 */

/**
 * Explicit interface for Auth0 context properties used by the application.
 * Defined explicitly rather than using Pick<Auth0ContextInterface> for:
 * - Better maintainability and clarity
 * - Reduced coupling to Auth0's internal type changes
 * - Easier to extend with custom properties if needed
 */
export interface SafeAuth0ContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: User | undefined;
    loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
    loginWithPopup: (options?: RedirectLoginOptions) => Promise<void>;
    logout: (options?: LogoutOptions) => void;
    getAccessTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>;
}

export const SafeAuth0Context = React.createContext<SafeAuth0ContextType | null>(null);

/**
 * Safe hook to get Auth0 context.
 * Returns safe defaults when used outside Auth0Provider (e.g., when self-managed auth is active).
 * This allows hooks to call this unconditionally while respecting React's Rules of Hooks.
 */
export const useSafeAuth0 = (): SafeAuth0ContextType => {
    const context = React.useContext(SafeAuth0Context);
    if (!context) {
        // Return safe defaults when used outside provider
        return {
            isAuthenticated: false,
            isLoading: false,
            user: undefined,
            loginWithRedirect: async () => {
                throw new Error('Auth0 is not enabled');
            },
            loginWithPopup: async () => {
                throw new Error('Auth0 is not enabled');
            },
            logout: () => {
                throw new Error('Auth0 is not enabled');
            },
            getAccessTokenSilently: async () => {
                throw new Error('Auth0 is not enabled');
            },
        };
    }
    return context;
};

interface SafeAuth0ProviderProps {
    children: React.ReactNode;
    value: SafeAuth0ContextType;
}

/**
 * Provider that wraps Auth0's context to make it safely accessible.
 * Used internally by Auth0ProviderWrapper to expose Auth0's context through SafeAuth0Context.
 */
export const SafeAuth0Provider: React.FC<SafeAuth0ProviderProps> = ({ children, value }) => {
    return <SafeAuth0Context.Provider value={value}>{children}</SafeAuth0Context.Provider>;
};

/**
 * Input type for useAuth0ContextValue hook.
 * Matches the shape returned by useAuth0() from @auth0/auth0-react.
 */
interface Auth0ContextInput {
    isAuthenticated: boolean;
    isLoading: boolean;
    user?: User;
    loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
    loginWithPopup: (options?: RedirectLoginOptions) => Promise<void>;
    logout: (options?: LogoutOptions) => void;
    getAccessTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>;
}

/**
 * Creates a memoized SafeAuth0ContextType value from Auth0 context.
 * Use this hook to bridge Auth0's useAuth0() output to SafeAuth0Provider.
 *
 * @example
 * const auth0 = useAuth0();
 * const contextValue = useAuth0ContextValue(auth0);
 * return <SafeAuth0Provider value={contextValue}>{children}</SafeAuth0Provider>;
 */
export const useAuth0ContextValue = (auth0: Auth0ContextInput): SafeAuth0ContextType => {
    return React.useMemo(
        () => ({
            isAuthenticated: auth0.isAuthenticated,
            isLoading: auth0.isLoading,
            user: auth0.user,
            loginWithRedirect: auth0.loginWithRedirect,
            loginWithPopup: auth0.loginWithPopup,
            logout: auth0.logout,
            getAccessTokenSilently: auth0.getAccessTokenSilently,
        }),
        [
            auth0.isAuthenticated,
            auth0.isLoading,
            auth0.user,
            auth0.loginWithRedirect,
            auth0.loginWithPopup,
            auth0.logout,
            auth0.getAccessTokenSilently,
        ],
    );
};
